/* eslint-disable sonarjs/no-duplicate-string */
import { toNDigits } from '@mail/cross-sizes-utils';
import { logger } from 'lib/logger';
import { generateImageApiCall } from 'reactApp/api/aiGeneration/GenerateImageApiCall';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import {
    cancelImageGeneration,
    changeImageGenerationLoaderText,
    generateImageAnimationStart,
    generateImageStart,
    generationScreenOpened,
    incrementAiGenerationsCount,
    incrementImageGenerationPercentage,
    resetImageGeneration,
    saveGeneratedImage,
    setGenerationPhotoScreenState,
    tryToShowMailSpacePromoModal,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.actions';
import aiPhotoGenerationClamp from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.clamp';
import {
    aiPhotoGenerationFolderId,
    aiPhotoGenerationFolderName,
    MAX_PROMPT_DWH_SYMBOLS,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.constants';
import {
    checkAiPhotoGenerationExists,
    getAiPhotoGenerationImageFileName,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.helpers';
import { aiPhotoGenerationImageHelper } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.image';
import { getAiGenerationImageState, getAiPhotoGenerationLimit } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.selectors';
import { EAiPhotoGenerationState } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.types';
import { getHomeFoldersTree } from 'reactApp/modules/home/home.selectors';
import { openPublishDialog } from 'reactApp/modules/modifying/modifying.actions';
import { createFolderRequest } from 'reactApp/modules/modifying/sagas/createFolder.saga';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { saveDocument } from 'reactApp/modules/uploading/uploading.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';
import { closeAiPhotoGenerationCancelGenerationModal } from 'reactApp/ui/AiPhotoGeneration/Modals/CancelGeneration/AiPhotoGenerationCancelGenerationModal.helpers';
import { closeFullScreenAiPhotoGeneration } from 'reactApp/ui/AiPhotoGeneration/Modals/MainScreen/FullScreenAiPhotoGenerationModal.helpers';
import {
    getAiPhotoGenerationMailSpacePromoModalShown,
    renderAiPhotoGenerationMailSpacePromoModal,
    setAiPhotoGenerationMailSpacePromoModalShown,
} from 'reactApp/ui/AiPhotoGeneration/Promo/Modal/MailSpacePromo/AiPhotoGenerationMailSpacePromo.helpers';
import { navigationRef } from 'reactApp/ui/App/components/NavigationSetter';
import { renderSelectFolderForAiImgDialog } from 'reactApp/ui/SelectFolderDialog/SelectFolderDialog.toolkit';
import { base64ToByteArray } from 'reactApp/utils/base64';
import { sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { aiPhotoGenerationsPlural } from 'reactApp/utils/pluralHelpers';
import { type Channel, channel } from 'redux-saga';
import { put as dispatchThunk, takeLatest } from 'redux-saga/effects';
import { call, cancel, delay, fork, put, select, take } from 'typed-redux-saga';

import { EStorageType } from '../storage/storage.types';

// eslint-disable-next-line max-lines-per-function
function* handleGenerateImageStart({ payload }: ReturnType<typeof generateImageStart>) {
    const { prompt, style } = payload;
    const abortController = new AbortController();

    yield* put(incrementAiGenerationsCount());

    const intervalTask = yield* fork(function* () {
        while (true) {
            yield* delay(500);
            yield* put(incrementImageGenerationPercentage());
        }
    });

    const intervalTask2 = yield* fork(function* () {
        while (true) {
            yield* delay(5000);
            yield* put(changeImageGenerationLoaderText());
        }
    });

    const cancelWatcher = yield* fork(function* () {
        yield* take(cancelImageGeneration.toString());
        abortController.abort();

        let text = '';

        const isCorp = yield* select(UserSelectors.isCorpUser);
        const isPaid = yield* select(UserSelectors.isPaidUser);
        if (isCorp || isPaid) {
            const attemptsLeft = yield* call(aiPhotoGenerationClamp.getAttemptsLeft);
            text = `Генерация остановлена. Осталось ещё ${attemptsLeft}\u00A0${aiPhotoGenerationsPlural(
                attemptsLeft
            )}\u00A0генераций на\u00A024\u00A0часа`;
        } else {
            text = `Генерация остановлена. Следующая генерация будет доступна через 24\u00A0часа`;
        }

        yield* put(
            showSnackbarAction({
                id: 'ai-photo-generation-cancel',
                text,
                type: SnackbarTypes.failure,
                closable: true,
                hideCloseIcon: true,
            })
        );
    });

    const startTime = performance.now();

    try {
        const resp = yield* call(generateImageApiCall, { prompt, style, signal: abortController.signal });

        closeAiPhotoGenerationCancelGenerationModal();

        if (resp.status === 200) {
            const endTime = performance.now() - startTime;
            const seconds = Math.round(endTime / 1000);

            const byteArray = base64ToByteArray(resp.data.image);
            aiPhotoGenerationImageHelper.saveImageData(byteArray);

            const size = toNDigits({ bytes: byteArray.byteLength, maxDigitsCount: 3 });

            const blob = new Blob([byteArray], { type: 'image/jpg' });
            const image = URL.createObjectURL(blob);

            yield* put(
                generateImageAnimationStart({
                    image,
                    size,
                    time: seconds,
                })
            );

            sendXray(['ai-gen', 'gen', 'suc'], { time: seconds });
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'generation-success',
                generation_time: seconds,
                generation_prompt: prompt?.slice(0, MAX_PROMPT_DWH_SYMBOLS),
                generation_size: size?.space,
                generation_style: style,
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user', 'generation_time']],
            });
        }
    } catch (error: any) {
        const endTime = performance.now() - startTime;
        const seconds = Math.round(endTime / 1000);

        if (error?.className === 'CancelError') {
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'generation-cancel',
                generation_time: seconds,
                generation_prompt: prompt?.slice(0, MAX_PROMPT_DWH_SYMBOLS),
                generation_style: style,
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user', 'generation_time']],
            });
        } else if (error?.className === 'ConnectionError') {
            logger.error(error);
            sendXray(['ai-gen', 'gen', 'err']);
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'generation-error',
                error_type: 'connection-error',
                error_description: error?.error_description,
                generation_time: seconds,
                generation_prompt: prompt?.slice(0, MAX_PROMPT_DWH_SYMBOLS),
                generation_style: style,
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user', 'error_type']],
            });

            yield* put(
                showSnackbarAction({
                    id: 'ai-photo-generation-connection-error',
                    text: 'Не удалось сгенерировать изображение — нет интернета',
                    type: SnackbarTypes.failure,
                    closable: true,
                    hideCloseIcon: true,
                })
            );
            yield* put(resetImageGeneration({ savePrompt: true }));
        } else {
            logger.error(error);
            sendXray(['ai-gen', 'gen', 'err']);
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'generation-error',
                error_type: (error?.error ?? '').toLowerCase(),
                error_description: error?.error_description,
                generation_time: seconds,
                generation_prompt: prompt?.slice(0, MAX_PROMPT_DWH_SYMBOLS),
                generation_style: style,
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user', 'error_type']],
            });

            let text = 'Не удалось сгенерировать изображение — попробуйте позже';
            if (error?.status === 400) {
                text = 'Изображение не может быть сгенерировано — запрос не соответствует правилам';
            } else if (error?.status === 403) {
                text = 'Изображение не может быть сгенерировано — небезопасный запрос';
            } else if (error?.status === 429) {
                text = 'Вы превысили лимит запросов — повторите попытку позже';
            }

            yield* put(
                showSnackbarAction({
                    id: 'ai-photo-generation-failure',
                    text,
                    type: SnackbarTypes.failure,
                    closable: true,
                    hideCloseIcon: true,
                })
            );
            yield* put(resetImageGeneration({ savePrompt: true }));
        }
    } finally {
        yield* cancel(intervalTask);
        yield* cancel(intervalTask2);
        yield* cancel(cancelWatcher);
    }
}

function* trySaveDocument(data: Uint8Array, home: string) {
    const resultChannel: Channel<any> = yield* call(channel);

    yield* put(
        saveDocument({
            home,
            fileContent: data,
            onResult: (res) => {
                resultChannel.put(res);
            },
        })
    );

    const result = yield* take(resultChannel);
    resultChannel.close();

    return result;
}

// eslint-disable-next-line max-lines-per-function, sonarjs/cognitive-complexity
function* handleSaveGeneratedImage({ payload }: ReturnType<typeof saveGeneratedImage>) {
    const { publishAfterSuccess = false } = payload;

    const data = aiPhotoGenerationImageHelper.getImageData();

    if (!data) {
        return;
    }

    const fileName = `${getAiPhotoGenerationImageFileName()}.jpg`;

    const folders = yield* select(getHomeFoldersTree);
    if (!checkAiPhotoGenerationExists(folders)) {
        try {
            yield* call(createFolderRequest, {
                folderName: aiPhotoGenerationFolderName,
                share: false,
                parent: ROOT_FOLDER_ID,
                storage: EStorageType.home,
                gaPrefix: 'ai-photo-generation',
                onSnackbarClick: () => {
                    closeFullScreenAiPhotoGeneration();
                },
            });
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'create-folder-suc',
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user']],
            });
        } catch (error) {
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'create-folder-err',
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user']],
            });
        }
    }

    let home = `${aiPhotoGenerationFolderId}/${fileName}`;
    let successText = 'Сохранено в Библиотеку генераций';
    let pathToFolder = aiPhotoGenerationFolderId;
    let { result, error } = yield* call(trySaveDocument, data, home);

    const size = aiPhotoGenerationImageHelper.getImageSize();
    const generation_size = size?.space;

    if (error || !result) {
        sendXray(['ai-gen', 'image-auto-save', 'err']);

        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'save-error',
            generation_size,
            error_type: 'folder',
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'error_type', 'generation_size']],
        });

        const uploadChannel: Channel<any> = yield* call(channel);

        renderSelectFolderForAiImgDialog({
            onSuccess: (answer) => {
                uploadChannel.put(answer);
            },
            onClose: () => {
                uploadChannel.put({ action: 'cancel' });
            },
        });

        let answer = yield* take(uploadChannel);
        while (answer.action === 'select') {
            answer = yield* take(uploadChannel);
        }

        uploadChannel.close();
        closePopupHelper(popupNames.SELECT_FOLDER_DIALOG);

        if (answer.action === 'upload') {
            home = `${answer.destination}/${fileName}`;
            const clearedFolder = answer.destination.indexOf('/') === 0 ? answer.destination.slice(1) : answer.destination;
            successText = clearedFolder ? `Сохранено в папку ${clearedFolder}` : 'Изображение сохранено';
            pathToFolder = answer.destination;
            const retry = yield* call(trySaveDocument, data, home);
            result = retry.result;
            error = retry.error;
        }

        if (answer.action === 'cancel') {
            sendXray(['ai-gen', 'image-save', 'skip']);
            error = null;
        }
    }

    if (error) {
        sendXray(['ai-gen', 'image-save', 'err']);

        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'save-error',
            generation_size,
            error_type: 'file',
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'error_type', 'generation_size']],
        });

        logger.error(error);
        yield* put(
            showSnackbarAction({
                id: 'ai-photo-generation-save-failure',
                text: 'Не удалось сохранить изображение. Проверьте подключение к интернету',
                type: SnackbarTypes.failure,
                closable: true,
                hideCloseIcon: true,
            })
        );
        return;
    }

    if (result) {
        yield* put(
            showSnackbarAction({
                id: 'ai-photo-generation-save-success',
                text: successText,
                type: SnackbarTypes.success,
                closable: true,
                hideCloseIcon: true,
                buttonText: 'Перейти',
                onShow: () => {
                    sendPaymentGa({
                        eventCategory: ECategoryGa.aiPhotoGeneration,
                        action: 'snackbar-show',
                        snackbar_type: 'suc-save-to-cloud',
                        dwh_enabled: true,
                        dwh_enabled_fields: [['paid_user', 'corp_user', 'snackbar_type']],
                    });
                },
                onButtonClick: () => {
                    sendPaymentGa({
                        eventCategory: ECategoryGa.aiPhotoGeneration,
                        action: 'snackbar-click',
                        snackbar_type: 'suc-save-to-cloud',
                        dwh_enabled: true,
                        dwh_enabled_fields: [['paid_user', 'corp_user', 'snackbar_type']],
                    });
                    navigationRef.current?.(`/home${pathToFolder}`);
                    closeFullScreenAiPhotoGeneration();
                },
            })
        );

        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'save-success',
            generation_size,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'generation_size']],
        });

        if (publishAfterSuccess) {
            yield* put(
                openPublishDialog({
                    item: { id: home },
                    publishFrom: 'ai-photo-generation',
                    onClose: () => {
                        reduxStore.dispatch(tryToShowMailSpacePromoModal());
                    },
                })
            );
        } else {
            yield handleTryToShowMailSpacePromoModal();
        }
    }
}

function* handleTryToShowMailSpacePromoModal() {
    const isCorp = yield* select(UserSelectors.isCorpUser);
    const isPaid = yield* select(UserSelectors.isPaidUser);
    if (!isCorp && !isPaid) {
        const mailSpacePromoShown = yield dispatchThunk(getAiPhotoGenerationMailSpacePromoModalShown());

        if (!mailSpacePromoShown) {
            yield dispatchThunk(setAiPhotoGenerationMailSpacePromoModalShown());
            renderAiPhotoGenerationMailSpacePromoModal();
        }
    }
}

function* handleAddGenerationRequest() {
    yield* call(aiPhotoGenerationClamp.addRequest);
}

function* handleMaybeClamp() {
    const limit = yield* select(getAiPhotoGenerationLimit);

    const canMakeRequest = yield* call(aiPhotoGenerationClamp.canMakeRequest, limit);
    if (!canMakeRequest) {
        yield* put(setGenerationPhotoScreenState({ state: EAiPhotoGenerationState.clamp }));
    }
}

function* handleMaybeClampOnScreenOpen() {
    const limit = yield* select(getAiPhotoGenerationLimit);
    const currentState = yield* select(getAiGenerationImageState);

    // Уже сгенерировали фото и ещё не сбросили состояние — продолжаем показывать его,
    // пока пользователь не выполнит действие (сохранение/новая генерация/сброс).
    if (currentState === EAiPhotoGenerationState.complete || currentState === EAiPhotoGenerationState.endAnimation) {
        return;
    }

    const canMakeRequest = yield* call(aiPhotoGenerationClamp.canMakeRequest, limit);
    if (!canMakeRequest) {
        yield* put(setGenerationPhotoScreenState({ state: EAiPhotoGenerationState.clamp }));
    } else if (currentState === EAiPhotoGenerationState.clamp) {
        yield* put(setGenerationPhotoScreenState({ state: EAiPhotoGenerationState.idle }));
    }
}

export function* watchAiGeneration() {
    yield takeLatest(generateImageStart.toString(), handleGenerateImageStart);
    yield takeLatest(saveGeneratedImage.toString(), handleSaveGeneratedImage);
    yield takeLatest(incrementAiGenerationsCount.toString(), handleAddGenerationRequest);
    yield takeLatest(tryToShowMailSpacePromoModal.toString(), handleTryToShowMailSpacePromoModal);
    yield takeLatest(generationScreenOpened.toString(), handleMaybeClampOnScreenOpen);
    yield takeLatest([resetImageGeneration.toString(), cancelImageGeneration.toString()], handleMaybeClamp);
}
